// src/lib/formatPrice.ts
//
// Format a price with the appropriate currency symbol + locale.
// Multi-currency support per M4: EUR (primary), USD, GBP.

export type Currency = "EUR" | "USD" | "GBP";

/**
 * Currency display configuration. Locale determines how numbers
 * are formatted (decimal separator, thousands grouping).
 */
const currencyConfig: Record<
  Currency,
  { locale: string; symbol: string; position: "before" | "after" }
> = {
  EUR: { locale: "fr-FR", symbol: "€", position: "after" }, // French formatting for BENCHMARK
  USD: { locale: "en-US", symbol: "$", position: "before" },
  GBP: { locale: "en-GB", symbol: "£", position: "before" },
};

/**
 * Format a price with the given currency.
 *
 * @param amount - The price (in the currency's smallest unit, e.g. 99.99)
 * @param currency - One of: EUR, USD, GBP
 * @returns A localized string like "99,00 €" or "$99.00"
 *
 * @example
 *   formatPrice(99, "EUR")     // "99,00 €"
 *   formatPrice(99, "USD")     // "$99.00"
 *   formatPrice(1234.5, "GBP") // "£1,234.50"
 */
export function formatPrice(amount: number, currency: Currency): string {
  const config = currencyConfig[currency];
  const formatted = new Intl.NumberFormat(config.locale, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  return config.position === "before"
    ? `${config.symbol}${formatted}`
    : `${formatted} ${config.symbol}`;
}